import Editor, { DiffEditor } from "@monaco-editor/react";
import { Grid, Button, Typography, Tooltip, SvgIcon, CircularProgress } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { useState, useRef, useEffect } from 'react';
import shortUuid from 'short-uuid';
import { useDispatch, useSelector } from 'react-redux';
import { getCodeEditStatus, getIsDocumentClicked, getReRender } from '../../../store/global/selectors';
import Requester from "../../../store/blueprint/svc";
import { invokePostService, invokeGetService, setReRender, setCodeEditStatus, setAiRequestEntity, updateAiAssistant } from "../../../store/global/actions";
import CodeSaveAlert from '../alerts/CodeSaveAlert';
import { makeStyles } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import DeleteIcon from '@material-ui/icons/Delete';
import SkeletonLoader from "../../atoms/loader/SkeletonLoader";
import { StarsIcon, AiCodeIcon } from '../icons';
import { getDetailsFromUrl } from "../../../services/blueprint";
import { IconButton } from "@material-ui/core";

const fetch = new Requester();

const b_suggestions = [
    {
        label: 'local',
        documentation: 'Keyword for declare variables',
        kind: 'Keyword',
        insertText: 'local',
    },
    {
        label: 'if',
        documentation: 'Reserved Keyword',
        kind: 'Keyword',
        insertText: 'if',
    },
    {
        label: 'for',
        documentation: 'Reserved Keyword',
        kind: 'Keyword',
        insertText: 'for',
    },
    {
        label: 'instance',
        documentation: 'Type: Instance',
        kind: 'Variable',
        insertText: 'instance',
    }
]

const useStyles = makeStyles((theme) => ({
    alertMessage: {
        width: '100%'
    },
    mainGrid: {
        margin: '0 !important',
        '& .MuiGrid-root:not(last-child)': {
            marginLeft: '0 !important',
            padding: '0 !important'
        }
    },
    iconBtn: {
        fontSize: "18px",
        color: "#1c548c",
        cursor: "pointer"
    },
    disabledBtn: {
        fontSize: "18px",
        color: "#97a3b4"
    },
    modalBtn: {
        textTransform: "none",
        padding: 5
    }
}));

const AI_STATE = {
    IDLE: "idle",
    GENERATING: "generating",
    PREVIEW: "preview",
    ACCEPTED: "accepted",
};

export default function CodeEditor({ onCompletion, onCancel, element, handlerType, editInProgress, setEventFromDelegate, setEventToDelegate, setHandlerReuseAlert, setHandlerAssociatedEvent, autoCompleteSuggestions, setAction }) {
    
    const classes = useStyles();
    const containerRef = useRef(null)
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const editorContainerRef = useRef(null);
    const aiDragRef = useRef({
        startX: 0,
        startY: 0,
        panelX: 0,
        panelY: 0,
        dragging: false,
    });
    const aiInputRef = useRef(null);

    const dispatch = useDispatch();
    
    const codeEditStatus = useSelector(getCodeEditStatus);
    const clickStatus = useSelector(getIsDocumentClicked);
    const reRender = useSelector(getReRender);

    
    const selectedEvent = element.item;
    const [allClusterlets, setAllClusterlets] = useState([]);
    const [validationMsg, setValidationMsg] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [currentHandler, setCurrentHandler] = useState(null);
    const [editorContent, setEditorContent] = useState('');
    const clusterlet = handlerType === 'onsend' ? element?.item?.src : element?.item?.dst;
    const [fetching, setFetching] = useState(true);
    const [openSaveAlert, setOpenSaveAlert] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const [messageSuggestion, setMessageSuggestions] = useState([])
    const [allSuggestions, setAllSuggestions] = useState([])
    const [allFields, setAllFields] = useState([])
    const [isFullScreen, setFullScreen] = useState(false)
    const [disableDeleteButton, setDisableDeleteButton] = useState(false)
    const [showAiPanel, setShowAiPanel] = useState(false);
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [aiPos, setAiPos] = useState({ x: 100, y: 80 });
    const [aiState, setAiState] = useState(AI_STATE.IDLE);


    const { entityId: transactionId } = getDetailsFromUrl();

    useEffect(() => {
        getHandlerAssociates()
    }, [reRender])

    useEffect(() => {
        setAllSuggestions(() => [...(new Set([...b_suggestions, ...autoCompleteSuggestions, ...messageSuggestion]))])
    }, [autoCompleteSuggestions, messageSuggestion])

    useEffect(() => {
        if (!monacoRef.current || !allSuggestions) {
            // console.log('monacoRef.current or allSuggestions not ready');
            return;
        }

        const monacoCompleteProvide = monacoRef.current.languages.registerCompletionItemProvider('lua', {
            provideCompletionItems: (model, position, context, token) => {
                const textUntilPosition = model.getValueInRange({ startLineNumber: position.lineNumber, startColumn: 1, endLineNumber: position.lineNumber, endColumn: position.column })
                const word = model.getWordUntilPosition(position);

                // Adjust range based on current word and position
                const range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: position.column
                };

                const previousText = getPreviousText(textUntilPosition);

                const filteredSuggestion = allSuggestions.filter(item => {
                    const splittedText = previousText.split('.');

                    for (let i = 0; i < splittedText.length; i++) {
                        let previous = splittedText[i]
                        const regex = new RegExp(`^${previousText}`)
                        if (item.insertText.includes(previous) && regex.test(item.insertText)) {
                            return true
                        }
                    }
                    return false
                })

                // Create suggestions with the updated range
                const suggestions = filteredSuggestion.map((suggestion) => ({
                    ...suggestion,
                    kind: monacoRef.current.languages.CompletionItemKind[suggestion.kind],
                    range: range,
                    insertText: getReplacedText(suggestion.insertText, textUntilPosition, word)
                }));

                return {
                    suggestions,
                };
            },
        });

        return () => {
            if (monacoCompleteProvide && typeof monacoCompleteProvide.dispose === 'function') {
                monacoCompleteProvide.dispose();
            }
        };
    }, [monacoRef.current, allSuggestions, autoCompleteSuggestions, editorContent]);



    useEffect(() => {
        // For triggering the save alert when closing the handler
        if (codeEditStatus) {
            if (currentHandler === null) {
                setOpenSaveAlert(true);
            } else {
                if (currentHandler?.data !== editorRef?.current?.getValue()) {
                    setOpenSaveAlert(true);
                }
            }
        }

    }, [clickStatus]);

    useEffect(() => {
        const structuredFields = getStructuredFields(allFields, element?.item?.message)
        setMessageSuggestions(makeMessageSuggestionList(structuredFields) || [])
    }, [allFields])

    useEffect(() => {
        // When exiting fullscreen and AI panel is open → center it
        if (!isFullScreen && showAiPanel && editorContainerRef.current) {
            const rect = editorContainerRef.current.getBoundingClientRect();

            const PANEL_WIDTH = 340;
            const PANEL_HEIGHT = 220;

            const centerX = rect.width / 4 - PANEL_WIDTH / 2;
            const centerY = rect.height / 2 - PANEL_HEIGHT / 2;

            setAiPos({
                x: Math.max(0, centerX),
                y: Math.max(0, centerY),
            });
        }
    }, [isFullScreen, showAiPanel]);

    useEffect(() => {
        if (showAiPanel && aiInputRef.current) {
            aiInputRef.current.focus();
        }
    }, [showAiPanel]);


    const getStructuredFields = (allFields, parent) => {
        let structuredFields = allFields?.filter(field => field.parent[0].id === parent);

        structuredFields.forEach(item => {
            item.children = getStructuredFields(allFields, item._id)
        })

        return structuredFields;
    }

    const makeMessageSuggestionList = (tree, prefix = "message") => {
        let list = [];

        tree.forEach((branch) => {
            list.push({
                label: `${prefix}.${branch.name} (${branch.attributes.type})`,
                kind: 'Variable',
                insertText: `${prefix}.${branch.name}`,
                documentation: `Type: ${branch.attributes.type}`,
                detail: `Type: ${branch.attributes.type}`
            })
            if (branch.children.length > 0) {
                list = [...list, ...makeMessageSuggestionList(branch.children, `${prefix}.${branch.name}`)]
            }
        })

        return list
    }

    const getReplacedText = (actualInsertText, textUntilPosition, wordEntered) => {
        let textToRemove = getPreviousText(textUntilPosition)
        textToRemove = textToRemove?.replace(getStringMatchRegex(wordEntered?.word || ''), '')

        return actualInsertText.replace(getStringMatchRegex(textToRemove), '')
    }

    const getPreviousText = (textUntilPosition) => {
        let trimmedText = textUntilPosition?.replace(/\s+/ig, '');
        let equalIndex = trimmedText.indexOf('=');
        let previousText = equalIndex === -1 ? trimmedText : trimmedText.slice(equalIndex + 1);

        return previousText
    }

    const getStringMatchRegex = (string) => new RegExp(string)


    const getHandlerAssociates = () => {
        let eId = element.id;
        let cId = (handlerType === 'onsend') ? element?.item?.src : element?.item?.dst;
        let type = handlerType;
        dispatch(invokeGetService('getHandlerAssociates', [eId, cId, type]))
            .then((response) => {
                setHandlerReuseAlert("");
                setHandlerAssociatedEvent(undefined);
                setAllClusterlets(response?.data?.clusterlets);
                setAllMessages(response?.data?.messages);
                setAllFields(response?.data?.fields)
                const associatedHandler = response?.data?.handlers.find(handler => handler.event == element.id && handler.type == handlerType && handler?.clusterlet === clusterlet);
                console.log({ associatedHandler })

                if (associatedHandler) {
                    setCurrentHandler(associatedHandler);
                    setDisableDeleteButton(false);   // checking the delete handler button need to be enabled
                    setEditorContent(associatedHandler.data);
                } else {
                    setEditorContent(prepareBoilerPlate());
                }
                checkHandlerReusability({ handlers: response?.data?.handlers, transactions: response?.data?.transactions, events: response?.data?.events, clusterlets: response?.data?.clusterlets, clusterlet });

                setFetching(false)
            })
    }

    // filtering first event in the transaction from all events
    const filterFirstEvent = (events, transactions, clusterlets) => {
        try {


            let firstEvents = []

            transactions.forEach(tr => {
                if (tr.events.length > 0) {

                    const minOrderEvent = tr.events.reduce((minOrder, event) => {
                        return event.order < minOrder.order ? event : minOrder;
                    }, tr.events[0]);

                    if (minOrderEvent !== undefined) {

                        let firstEvent = events.find(ev => ev._id == minOrderEvent?.id)

                        if (firstEvent !== undefined) {
                            const src = clusterlets.find(item => item._id.toString() == firstEvent.src)
                            if (src !== undefined && src.type == 1) firstEvents.push(firstEvent)

                        }

                    }
                }
            })

            return firstEvents;


        } catch (error) {
            console.error(error);
            return [];
        }
    }


    const checkHandlerReusability = ({ handlers, transactions, events, clusterlets }) => {
        try {

            const currentEvent = events.find(event => event._id == selectedEvent._id);

            if (handlerType == "onsend" && currentEvent.attributes.onsend == false) {
                setHandlerReuseAlert("");
                return
            }

            if (handlerType == "onrecv" && currentEvent.attributes.onrecv == false) {
                setHandlerReuseAlert("");
                return
            }

            // first events in all transactions
            let firstEventFronDel = filterFirstEvent(events, transactions, clusterlets) || [];

            let handler = null;
            if (handlerType == "onsend") {
                handler = handlers.find(item => item.event == currentEvent._id && item.clusterlet === clusterlet && item.type == "onsend")
            } else {
                handler = handlers.find(item => item.event == currentEvent._id && item.clusterlet === clusterlet && item.type == "onrecv")
            }

            const handlerCandidates = findHandlerCandidates(currentEvent, events, clusterlets, firstEventFronDel)

            if (handler == null && handlerCandidates?.length === 0) {
                setHandlerReuseAlert("");
                return;
            }

            // associated handler present
            if (handler) {
                if (handlerCandidates.length != 0) {
                    createAlertMessageForReusedHandler([...handlerCandidates, currentEvent], transactions)
                } else {
                    setHandlerReuseAlert("");
                }
            } else {
                // scope for reusing handler
                if (handlerCandidates.length != 0) {
                    const handler = findHandler(handlerCandidates, handlers, handlerType, clusterlet)
                    if (!handler) {
                        setHandlerReuseAlert("");
                        return
                    }
                    const associatedEvent = events.find(event => event._id == handler.event)
                    
                    setCurrentHandler(handler);
                    setEditorContent(handler.data);
                    createAlertMessageForReusedHandler([...handlerCandidates, currentEvent], transactions)
                    setDisableDeleteButton(false);
                    setHandlerAssociatedEvent({ ...associatedEvent, handler: handler._id });
                } else {
                    //console.log("No Handler candidates present")
                    setDisableDeleteButton(true);
                    setHandlerReuseAlert("");
                }

            }



        } catch (error) {
            console.error(error)
        }
    }

    const findHandler = (candidates, handlers, handlerType, clusterlet) => {
        try {

            const eventIds = candidates.map(item => item._id)

            let reusedHandler = null
            eventIds?.forEach(item => {
                handlers.forEach(handler => {
                    if (handler.type == handlerType && handler?.clusterlet === clusterlet && handler.event == item) {
                        reusedHandler = handler
                    }
                })
            })

            return reusedHandler;

        } catch (error) {
            console.error(error);
            return null;
        }
    }

    const findHandlerCandidates = (currentEvent, allEvents, clusterlets, firstEventFronDel) => {
        try {


            const filteredEvents = allEvents?.filter(item => item._id.toString() !== currentEvent?._id)
            const candidates = filteredEvents.filter(event => {

                // if (!firstEventFronDel.map(ev => ev._id).includes(event._id) && currentEvent._id != event._id) {
                if (handlerType == "onsend") {
                    if (event.name == currentEvent.name && event.message == currentEvent.message && event.src == currentEvent.src) {
                        return event
                    }
                } else {
                    if (event.name == currentEvent.name && event.message == currentEvent.message && event.dst == currentEvent.dst) {
                        return event
                    }
                }

                // }

            });
            return candidates


        } catch (error) {
            console.error(error);
            return [];
        }
    }

    const createAlertMessageForReusedHandler = (candidates, transactions) => {
        try {

            const messageItems = [];
            candidates.forEach(event => {
                // console.log("candidate event: ", event)
                let txn = transactions.find(txn => {
                    let txnEventIds = txn.events.map(item => item.id)
                    if (txnEventIds.includes(event._id)) {
                        return txn
                    }
                })
                if (txn) messageItems.push(`${event.name}( ${txn.name} )`)
            })
            // console.log("message items: ", messageItems)
            const message = `This handler is shared with the events - ${messageItems.join(", ")}`;

            // console.log("Hanlder alert message: ", message)
            setHandlerReuseAlert(message);


        } catch (error) {
            console.error(error);
            return "";
        }
    }


    let action = 'add';

    let handlerArguments = ['instance', 'transaction', 'message'];
    let messageType = allMessages.find(item => item._id === selectedEvent?.message);
    if (handlerType !== 'onsend') handlerArguments.push('messageSource');
    const handlerFunctionName = `function ${handlerType}_${element.name}${messageType ? ("_" + messageType?.name) : ""} (${handlerArguments.join(', ')})`;

    allClusterlets?.forEach(clusterlet => {
        if (element.item.src === clusterlet._id && clusterlet.type === 1) {
            if (setEventFromDelegate !== undefined) setEventFromDelegate(true)
            return;
        } else if (element.item.dst === clusterlet._id && clusterlet.type === 1) {
            if (setEventToDelegate !== undefined) setEventToDelegate(true)
            return;
        }
    })

    const prepareBoilerPlate = () => {

        let boilerPlate = '\n--start here--\n\n\n';
        boilerPlate += '--[[\n\n';
        boilerPlate += 'Define handler action\n\n';
        boilerPlate += 'instance - the clusterlet instance\n';
        boilerPlate += 'transaction - transaction context\n';
        boilerPlate += 'message - actual message\n\n';
        boilerPlate += '--]]\n\n\n'
        boilerPlate += '--end here--\n\n\n'
        return boilerPlate;
    }

    action = currentHandler !== undefined ? 'edit' : 'add';

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco

        const handleEditorChange = () => {
            const newValue = editorRef.current.getValue();
            setEditorContent(newValue); // Update state with new content
        };

        // Subscribe to content changes
        const model = editorRef.current.getModel();
        model.onDidChangeContent(handleEditorChange);
        //console.log({ autoCompleteSuggestions });

    }

    const handleEditorChange = (value, event) => {
        setEditorContent(value || "");
        if (codeEditStatus === false) {
            dispatch(setCodeEditStatus(true));
        } else {
            if (currentHandler !== null && currentHandler.data == editorRef.current.getValue()) {
                dispatch(setCodeEditStatus(false));
            }
        }
    }


    const onClose = () => {
        if (codeEditStatus) {
            setOpenSaveAlert(true);
        } else {
            onCancel();
        }
    }
    const onChooseLeave = () => {
        setOpenSaveAlert(false);
        dispatch(setCodeEditStatus(false));
    }


    const onSave = async ({ data, type } = {}) => {
        const code = data ?? editorRef.current.getValue();
        setLoading(true);
        const handler = {
            id: currentHandler ? currentHandler.id : shortUuid.generate(),
            data: code === "" ? " " : code,
            type: handlerType,
            // event: element.item._id,
            event: currentHandler ? currentHandler.event : element.item._id,
            clusterlet
        }


        let status = code == '' ? false : true;


        if (type !== 'anyway') {
            let validationResult = await fetch.post(fetch.route.luaValidate, { code });
            if (validationResult.status == false) {
                const errorMessage = validationResult.message.replace(/undefined/g, "").replace(/\[[^0-9](.*?)\]/g, "");
                setValidationMsg(errorMessage);
                setLoading(false);
                setOpenSaveAlert(false)
                setIsUpdating(false);
                return;
            } else {
                setValidationMsg(undefined);
            }
        }

        // console.log({handler})
        dispatch(invokePostService('createHandler', { handlerData: handler }, []))
            .then(response => {
                dispatch(setCodeEditStatus(false));
                setOpenSaveAlert(false);
                setLoading(false);
                setIsUpdating(false);
                if (response?.status) {
                    setDisableDeleteButton(false);
                    setValidationMsg(undefined);
                    dispatch(setReRender());
                    onCompletion(action, handlerType);
                    getHandlerAssociates();
                }
            })
    }

    const handleFullScreen = () => {
        const element = containerRef.current;
        if (document.fullscreenElement) {
            document.exitFullscreen();
            setFullScreen(false)
        } else {
            setFullScreen(true)
            element?.requestFullscreen();
        }
    }



    useEffect(() => {
        function onFullScreenChange() {
            if (
                !document.fullscreenElement &&
                !document.webkitFullscreenElement &&
                !document.mozFullScreenElement &&
                !document.msFullscreenElement
            ) {
                setFullScreen(false);
            }
        }
    
        document.addEventListener("fullscreenchange", onFullScreenChange);
        document.addEventListener("webkitfullscreenchange", onFullScreenChange);
        document.addEventListener("mozfullscreenchange", onFullScreenChange);
        document.addEventListener("MSFullscreenChange", onFullScreenChange);
    
        return () => {
            document.removeEventListener("fullscreenchange", onFullScreenChange);
            document.removeEventListener("webkitfullscreenchange", onFullScreenChange);
            document.removeEventListener("mozfullscreenchange", onFullScreenChange);
            document.removeEventListener("MSFullscreenChange", onFullScreenChange);
            document.removeEventListener("mousemove", onAiMouseMove);
            document.removeEventListener("mouseup", onAiMouseUp);
        };
    }, []);
    


    /* ---------------- AI prompt panel drag ---------------- */
    const onAiMouseDown = (e) => {
        aiDragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            panelX: aiPos.x,
            panelY: aiPos.y,
            dragging: true,
        };

        document.addEventListener("mousemove", onAiMouseMove);
        document.addEventListener("mouseup", onAiMouseUp);
    };

    const onAiMouseMove = (e) => {
        if (!aiDragRef.current.dragging) return;

        const dx = e.clientX - aiDragRef.current.startX;
        const dy = e.clientY - aiDragRef.current.startY;

        const container = editorContainerRef.current.getBoundingClientRect();

        let newX = aiDragRef.current.panelX + dx;
        let newY = aiDragRef.current.panelY + dy;

        newX = Math.max(0, Math.min(newX, container.width - 380));
        newY = Math.max(0, Math.min(newY, container.height - 230));

        setAiPos({ x: newX, y: newY });
    };

    const onAiMouseUp = () => {
        aiDragRef.current.dragging = false;

        document.removeEventListener("mousemove", onAiMouseMove);
        document.removeEventListener("mouseup", onAiMouseUp);
    };

    const handleAiGenerate = async () => {
        if (!aiPrompt.trim() || aiLoading) return;

        setAiLoading(true);
        setAiState(AI_STATE.GENERATING);
        dispatch(setAiRequestEntity("handler"))
        dispatch(updateAiAssistant({ loading: true }));

        const code = editorRef.current.getValue();

        try {
            const res = await dispatch(invokePostService('generateHandler', { prompt: aiPrompt, code, transactionId, eventId: element.id, handlerType }, []));
            if (res?.status) {
                if (res?.data?.code) {
                    setEditorContent(res?.data?.code)
                    editorRef.current.setValue(res.data.code);
                }
                setAiState(AI_STATE.PREVIEW);
            }
        } catch (err) {
            console.error("AI Error:", err);
        }

        setAiLoading(false);
        dispatch(setAiRequestEntity(null))
        dispatch(updateAiAssistant({ loading: false }));
    };

    const handleAccept = () => {
        onSave({ data: editorContent });
        setAiState(AI_STATE.IDLE);
        setShowAiPanel(false);
        setEditorContent(editorContent); 
        setAiPrompt("");
    };


    const handleUndo = () => {
        setEditorContent(currentHandler?.data || prepareBoilerPlate()); 
        setAiState(AI_STATE.IDLE);
        dispatch(setCodeEditStatus(false));
        setShowAiPanel(false);
        setAiPrompt("");
    };

    const handleCloseModal = () => {
        setEditorContent(currentHandler?.data || prepareBoilerPlate()); 
        dispatch(setCodeEditStatus(false));
        setShowAiPanel(false);
        setAiPrompt("");
        setAiState(AI_STATE.IDLE);
    }

    const handleAiReGenerate = () => {
        setEditorContent(currentHandler?.data || prepareBoilerPlate()); 
        handleAiGenerate();
    }

    const isIconBtnDisabled = loading || aiLoading || aiState === AI_STATE.PREVIEW;

    return (

        <Grid container data-ui-tour-id="code-editor" direction={"row"} ref={containerRef} style={{ padding: "0 !important" }} className={` codeEditorContainer`}>
            {validationMsg !== undefined &&
                <Grid item xs={12} >
                    <Alert severity="error" classes={{
                        message: classes.alertMessage,
                    }}>
                        <Grid container id="handlerErrorContainer">
                            <Grid item xs={12}>
                                {validationMsg}
                            </Grid>
                            <Grid item xs={12} style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button onClick={() => { setIsUpdating(true); onSave({ type: 'anyway' }); }} disabled={isUpdating} style={{ backgroundColor: '#FFA726', color: '#fff', textTransform: 'capitalize' }} variant="contained" id="update_handler">
                                    {isUpdating ? 'Updating..' : 'Update Anyway'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Alert>
                </Grid>
            }
            {fetching && <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                <SkeletonLoader type='code-editor' />
            </Grid>}
            {!fetching && <Grid item xs={12} className={classes.mainGrid} style={{ padding: 0, marginLeft: '20px' }}>
                <Grid style={{ display: "flex", justifyContent: "space-between", paddingRight: "10px !important", backgroundColor: '#d5ded6', width: '100%' }}>
                    <Typography className="handlerFunctionName" style={{ padding: '5px 5px', fontWeight: 'bold', fontStyle: 'italic' }}> {handlerFunctionName} </Typography>
                    <Grid style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Tooltip title="Code with AI" arrow>
                            <span>
                                <AiCodeIcon
                                    className={classes.iconBtn}
                                    onClick={() => setShowAiPanel((p) => !p)}
                                    id="ai_code"
                                    style={{ fontSize: '20px', marginTop: 4 }}
                                    disabled={aiLoading}
                                />
                            </span>
                        </Tooltip>
                        <Tooltip title="Remove handler" arrow>
                            <IconButton
                                data-ui-tour-id="handler-delete-btn"
                                disabled={disableDeleteButton || isIconBtnDisabled}
                                onClick={() => setAction({ action: "delete_handler" })}
                                id="delete_handler"
                                style={{ padding: 0 }}
                            >
                                <DeleteIcon
                                    className={(disableDeleteButton || isIconBtnDisabled) ? classes.disabledBtn : classes.iconBtn}
                                    color="secondary"
                                />
                            </IconButton>

                        </Tooltip>
                        {
                            isFullScreen ?
                                <Tooltip title="Exit Fullscreen">
                                    <FullscreenExitIcon data-ui-tour-id="fullscreen-btn" className={classes.iconBtn} style={{ fontSize: '20px' }} onClick={handleFullScreen} />
                                </Tooltip>
                                :
                                <Tooltip title="Fullscreen">
                                    <FullscreenIcon data-ui-tour-id="fullscreen-btn" className={classes.iconBtn} style={{ fontSize: '20px' }} onClick={handleFullScreen} />
                                </Tooltip>

                        }
                        <IconButton
                            data-ui-tour-id="in-editor-save"
                            disabled={isIconBtnDisabled}
                            onClick={() => {
                                setLoading(true);
                                onSave();
                            }}
                            style={{ marginRight: "10px", padding: 0 }}
                        >
                            <SaveIcon className={isIconBtnDisabled ? classes.disabledBtn : classes.iconBtn} />

                            <span
                                id="save_handler"
                                style={{
                                    marginTop: 3,
                                    fontSize: 14,
                                    fontStyle: loading ? "italic" : "inherit",
                                    color: isIconBtnDisabled ? "#97a3b4" : "#1c548c"
                                }}
                            >
                                {loading ? "Saving.." : "Save"}
                            </span>
                        </IconButton>

                    </Grid>
                </Grid>
                <div
                    ref={editorContainerRef}
                    style={{
                        position: "relative",
                    }}
                >
                    {/* AI Prompt Panel */}
                    {showAiPanel && (
                        <div
                            style={{
                                position: "absolute",
                                left: aiPos.x,
                                top: aiPos.y,
                                width: 380,
                                height: 230,
                                background: "#1e1e1e",
                                border: "1px solid #333",
                                borderRadius: 8,
                                zIndex: 50,
                                display: "flex",
                                flexDirection: "column",
                                boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
                            }}
                        >
                            <div
                                onMouseDown={onAiMouseDown}
                                style={{
                                    height: 36,
                                    background: "#252526",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "0 10px",
                                    cursor: "move",
                                    userSelect: "none",
                                    borderBottom: "1px solid #333",
                                }}
                            >
                                <span style={{ color: "#fff", fontSize: 13 }}>
                                    AI Assistant
                                </span>

                                <button
                                    onClick={handleCloseModal}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#aaa",
                                        cursor: "pointer",
                                    }}
                                >
                                    ✕
                                </button>
                            </div>

                            <div
                                style={{
                                    flex: 1,
                                    padding: 8,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                }}
                            >
                                <textarea
                                    ref={aiInputRef}
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    placeholder="Describe what to generate..."
                                    style={{
                                        flex: 1,
                                        resize: "none",
                                        background: "#121212",
                                        color: "#fff",
                                        border: "1px solid #333",
                                        borderRadius: 4,
                                        padding: 6,
                                    }}
                                />
                                {aiState !== AI_STATE.PREVIEW && <Button
                                    onClick={handleAiGenerate}
                                    disabled={!aiPrompt.trim() || aiLoading}
                                    color="primary"
                                    variant="contained"
                                    startIcon={
                                        aiLoading ? (
                                            <CircularProgress size={18} style={{ color: "#fff" }} />
                                        ) : (
                                            <StarsIcon style={{ fontSize: 18 }} />
                                        )
                                    }
                                    style={{
                                        color: "#fff",
                                        borderRadius: 4,
                                        padding: "6px 12px",
                                        fontWeight: "bold",
                                        textTransform: "none",
                                        cursor: aiLoading ? "not-allowed" : "pointer",
                                        opacity: aiLoading ? 0.8 : 1,
                                    }}
                                    disableElevation
                                    disableRipple
                                >
                                    {aiLoading ? "Generating.." : "Generate"}
                                </Button>}

                                {aiState === AI_STATE.PREVIEW && (
                                    <Grid container spacing={1} alignItems="center" >
                                        <Grid item>
                                            <Button
                                                className={classes.modalBtn}
                                                size={"small"}
                                                variant="contained"
                                                onClick={handleUndo}
                                            >
                                                Revert
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                className={classes.modalBtn}
                                                size={"small"}
                                                variant="contained"
                                                style={{ backgroundColor: "#00c853" }}
                                                onClick={handleAccept}
                                                disableElevation
                                                disableRipple
                                            >
                                                Accept
                                            </Button>
                                        </Grid>

                                        <Grid item>
                                            <Button
                                                className={classes.modalBtn}
                                                size={"small"}
                                                variant="contained"
                                                color="primary"
                                                onClick={handleAiReGenerate}
                                                disableElevation
                                                disableRipple
                                            >
                                                Generate Again
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}
                            </div>
                        </div>
                    )}

                    {aiState !== AI_STATE.PREVIEW && <Editor
                        height={isFullScreen ? "95vh" : "60vh"}
                        defaultLanguage="lua"
                        language="lua"
                        theme="vs-dark"
                        value={editorContent}
                        onMount={handleEditorDidMount}
                        onChange={handleEditorChange}
                        style={{ border: "0px" }}
                        options={{
                            fontSize: 12, // Set initial font size to 12
                            wordWrap: 'on', // Optional: Enable word wrap
                            automaticLayout: true, // Adjusts to container size
                            scrollBeyondLastLine: false, // Prevent extra scrolling
                            mouseWheelZoom: true // Enable Ctrl + Scroll to zoom
                        }}
                    />}
                    {aiState === AI_STATE.PREVIEW && <DiffEditor
                        height={isFullScreen ? "95vh" : "60vh"}
                        defaultLanguage="lua"
                        language="lua"
                        theme="vs-dark"
                        original={currentHandler?.data || ""}
                        modified={editorContent}
                        options={{
                            // renderSideBySide: false,
                            readOnly: true,
                            minimap: { enabled: false },
                            fontSize: 14,
                            wordWrap: "on",
                            automaticLayout: true,
                            scrollBeyondLastLine: false, // Prevent extra scrolling
                            mouseWheelZoom: true // Enable Ctrl + Scroll to zoom
                        }}
                    />}
                </div>
                <Typography style={{ padding: '5px 5px', fontWeight: 'bold', fontStyle: 'italic', backgroundColor: '#d5ded6', marginTop: '-5px' }}>end</Typography>
            </Grid>}

            {openSaveAlert && <CodeSaveAlert
                onClose={onChooseLeave}
                onSave={onSave}
            />}

        </Grid>

    )
}